import {
  ConditionField,
  ConditionOperator,
  ConditionValueType,
  RuleActionType,
} from "./enums";

/**
 * Static metadata describing each condition field: what value type it expects
 * and which operators are valid. The rule builder reads this to render the
 * correct editor and operator list, so adding a new field is a one-line change.
 */
export interface IConditionFieldMeta {
  field: ConditionField;
  /** Translation key for the field label. */
  labelKey: string;
  valueType: ConditionValueType;
  operators: ConditionOperator[];
  /**
   * For ENTITY/MULTI_ENTITY fields, the entity kind the selector should query
   * (events, promoters, products, categories, campaigns).
   */
  entityKind?: "event" | "promoter" | "product" | "category" | "campaign";
  /** For ENUM fields, the enum option values (UPPER_SNAKE). */
  enumValues?: string[];
}

const EQUALITY = [ConditionOperator.EQUALS, ConditionOperator.NOT_EQUALS];
const MEMBERSHIP = [ConditionOperator.IN, ConditionOperator.NOT_IN];
const NUMERIC = [
  ConditionOperator.EQUALS,
  ConditionOperator.GREATER_THAN,
  ConditionOperator.GREATER_THAN_OR_EQUAL,
  ConditionOperator.LESS_THAN,
  ConditionOperator.LESS_THAN_OR_EQUAL,
  ConditionOperator.BETWEEN,
];
const BOOLEAN_OPS = [ConditionOperator.IS_TRUE, ConditionOperator.IS_FALSE];

/** Operators grouped by value type, for generic editors. */
export const OPERATORS_BY_VALUE_TYPE: Record<ConditionValueType, ConditionOperator[]> = {
  [ConditionValueType.STRING]: [
    ...EQUALITY,
    ConditionOperator.CONTAINS,
    ConditionOperator.STARTS_WITH,
  ],
  [ConditionValueType.NUMBER]: NUMERIC,
  [ConditionValueType.MONEY]: NUMERIC,
  [ConditionValueType.BOOLEAN]: BOOLEAN_OPS,
  [ConditionValueType.DATE]: NUMERIC,
  [ConditionValueType.DATE_RANGE]: [ConditionOperator.BETWEEN],
  [ConditionValueType.ENTITY]: EQUALITY,
  [ConditionValueType.ENUM]: EQUALITY,
  [ConditionValueType.MULTI_ENTITY]: MEMBERSHIP,
  [ConditionValueType.MULTI_ENUM]: MEMBERSHIP,
};

/** The full field catalogue used by the rule builder. */
export const CONDITION_FIELD_METADATA: Record<ConditionField, IConditionFieldMeta> = {
  [ConditionField.MEMBER_TYPE]: {
    field: ConditionField.MEMBER_TYPE,
    labelKey: "rewards.condition.member_type",
    valueType: ConditionValueType.ENUM,
    operators: EQUALITY,
    enumValues: ["CUSTOMER", "PROMOTER"],
  },
  [ConditionField.PURCHASE_TYPE]: {
    field: ConditionField.PURCHASE_TYPE,
    labelKey: "rewards.condition.purchase_type",
    valueType: ConditionValueType.MULTI_ENUM,
    operators: MEMBERSHIP,
    enumValues: ["EVENT_TICKET", "MERCHANDISE", "OTA_TRAVEL", "PROMOTER_SALE", "OTHER"],
  },
  [ConditionField.EVENT_ID]: {
    field: ConditionField.EVENT_ID,
    labelKey: "rewards.condition.event",
    valueType: ConditionValueType.MULTI_ENTITY,
    operators: MEMBERSHIP,
    entityKind: "event",
  },
  [ConditionField.EVENT_CATEGORY]: {
    field: ConditionField.EVENT_CATEGORY,
    labelKey: "rewards.condition.event_category",
    valueType: ConditionValueType.MULTI_ENTITY,
    operators: MEMBERSHIP,
    entityKind: "category",
  },
  [ConditionField.PRODUCT_ID]: {
    field: ConditionField.PRODUCT_ID,
    labelKey: "rewards.condition.product",
    valueType: ConditionValueType.MULTI_ENTITY,
    operators: MEMBERSHIP,
    entityKind: "product",
  },
  [ConditionField.PRODUCT_CATEGORY]: {
    field: ConditionField.PRODUCT_CATEGORY,
    labelKey: "rewards.condition.product_category",
    valueType: ConditionValueType.MULTI_ENTITY,
    operators: MEMBERSHIP,
    entityKind: "category",
  },
  [ConditionField.PROMOTER_ID]: {
    field: ConditionField.PROMOTER_ID,
    labelKey: "rewards.condition.promoter",
    valueType: ConditionValueType.MULTI_ENTITY,
    operators: MEMBERSHIP,
    entityKind: "promoter",
  },
  [ConditionField.CUSTOMER_SEGMENT]: {
    field: ConditionField.CUSTOMER_SEGMENT,
    labelKey: "rewards.condition.customer_segment",
    valueType: ConditionValueType.STRING,
    operators: [...EQUALITY, ...MEMBERSHIP],
  },
  [ConditionField.SALES_CHANNEL]: {
    field: ConditionField.SALES_CHANNEL,
    labelKey: "rewards.condition.sales_channel",
    valueType: ConditionValueType.MULTI_ENUM,
    operators: MEMBERSHIP,
    enumValues: ["WEB", "MOBILE_APP", "BOX_OFFICE", "CALL_CENTER", "PARTNER", "PROMOTER_PORTAL"],
  },
  [ConditionField.CURRENCY]: {
    field: ConditionField.CURRENCY,
    labelKey: "rewards.condition.currency",
    valueType: ConditionValueType.ENUM,
    operators: EQUALITY,
    enumValues: ["USD", "MXN", "EUR"],
  },
  [ConditionField.MIN_PURCHASE_SUBTOTAL]: {
    field: ConditionField.MIN_PURCHASE_SUBTOTAL,
    labelKey: "rewards.condition.min_subtotal",
    valueType: ConditionValueType.MONEY,
    operators: [ConditionOperator.GREATER_THAN_OR_EQUAL, ConditionOperator.GREATER_THAN],
  },
  [ConditionField.MAX_PURCHASE_SUBTOTAL]: {
    field: ConditionField.MAX_PURCHASE_SUBTOTAL,
    labelKey: "rewards.condition.max_subtotal",
    valueType: ConditionValueType.MONEY,
    operators: [ConditionOperator.LESS_THAN_OR_EQUAL, ConditionOperator.LESS_THAN],
  },
  [ConditionField.TICKET_QUANTITY]: {
    field: ConditionField.TICKET_QUANTITY,
    labelKey: "rewards.condition.ticket_quantity",
    valueType: ConditionValueType.NUMBER,
    operators: NUMERIC,
  },
  [ConditionField.MERCHANDISE_QUANTITY]: {
    field: ConditionField.MERCHANDISE_QUANTITY,
    labelKey: "rewards.condition.merchandise_quantity",
    valueType: ConditionValueType.NUMBER,
    operators: NUMERIC,
  },
  [ConditionField.BOOKING_TYPE]: {
    field: ConditionField.BOOKING_TYPE,
    labelKey: "rewards.condition.booking_type",
    valueType: ConditionValueType.MULTI_ENUM,
    operators: MEMBERSHIP,
    enumValues: ["HOTEL", "FLIGHT", "CAR_RENTAL", "PACKAGE", "TOUR"],
  },
  [ConditionField.MEMBER_TIER]: {
    field: ConditionField.MEMBER_TIER,
    labelKey: "rewards.condition.member_tier",
    valueType: ConditionValueType.STRING,
    operators: [...EQUALITY, ...MEMBERSHIP],
  },
  [ConditionField.FIRST_PURCHASE]: {
    field: ConditionField.FIRST_PURCHASE,
    labelKey: "rewards.condition.first_purchase",
    valueType: ConditionValueType.BOOLEAN,
    operators: BOOLEAN_OPS,
  },
  [ConditionField.REPEAT_PURCHASE]: {
    field: ConditionField.REPEAT_PURCHASE,
    labelKey: "rewards.condition.repeat_purchase",
    valueType: ConditionValueType.BOOLEAN,
    operators: BOOLEAN_OPS,
  },
  [ConditionField.DATE_RANGE]: {
    field: ConditionField.DATE_RANGE,
    labelKey: "rewards.condition.date_range",
    valueType: ConditionValueType.DATE_RANGE,
    operators: [ConditionOperator.BETWEEN],
  },
  [ConditionField.DAY_OF_WEEK]: {
    field: ConditionField.DAY_OF_WEEK,
    labelKey: "rewards.condition.day_of_week",
    valueType: ConditionValueType.MULTI_ENUM,
    operators: MEMBERSHIP,
    enumValues: ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"],
  },
  [ConditionField.CAMPAIGN_CODE]: {
    field: ConditionField.CAMPAIGN_CODE,
    labelKey: "rewards.condition.campaign_code",
    valueType: ConditionValueType.ENTITY,
    operators: EQUALITY,
    entityKind: "campaign",
  },
  [ConditionField.PROMOTER_PERFORMANCE]: {
    field: ConditionField.PROMOTER_PERFORMANCE,
    labelKey: "rewards.condition.promoter_performance",
    valueType: ConditionValueType.NUMBER,
    operators: NUMERIC,
  },
};

/** Metadata describing each rule action type for the action editor. */
export interface IRuleActionMeta {
  type: RuleActionType;
  labelKey: string;
  /** Translation key describing the `amount` unit. */
  amountUnitKey: string;
  /** Whether this action multiplies base points rather than adding them. */
  isMultiplier: boolean;
}

export const RULE_ACTION_METADATA: Record<RuleActionType, IRuleActionMeta> = {
  [RuleActionType.FIXED_POINTS]: {
    type: RuleActionType.FIXED_POINTS,
    labelKey: "rewards.action.fixed_points",
    amountUnitKey: "rewards.action.unit_points",
    isMultiplier: false,
  },
  [RuleActionType.POINTS_PER_CURRENCY_UNIT]: {
    type: RuleActionType.POINTS_PER_CURRENCY_UNIT,
    labelKey: "rewards.action.points_per_currency",
    amountUnitKey: "rewards.action.unit_points_per_currency",
    isMultiplier: false,
  },
  [RuleActionType.POINTS_PER_ITEM]: {
    type: RuleActionType.POINTS_PER_ITEM,
    labelKey: "rewards.action.points_per_item",
    amountUnitKey: "rewards.action.unit_points_per_item",
    isMultiplier: false,
  },
  [RuleActionType.PERCENTAGE_MULTIPLIER]: {
    type: RuleActionType.PERCENTAGE_MULTIPLIER,
    labelKey: "rewards.action.percentage_multiplier",
    amountUnitKey: "rewards.action.unit_percent",
    isMultiplier: true,
  },
  [RuleActionType.FIXED_MULTIPLIER]: {
    type: RuleActionType.FIXED_MULTIPLIER,
    labelKey: "rewards.action.fixed_multiplier",
    amountUnitKey: "rewards.action.unit_multiplier",
    isMultiplier: true,
  },
  [RuleActionType.TIER_QUALIFICATION_POINTS]: {
    type: RuleActionType.TIER_QUALIFICATION_POINTS,
    labelKey: "rewards.action.tier_qualification_points",
    amountUnitKey: "rewards.action.unit_points",
    isMultiplier: false,
  },
  [RuleActionType.BONUS_POINTS]: {
    type: RuleActionType.BONUS_POINTS,
    labelKey: "rewards.action.bonus_points",
    amountUnitKey: "rewards.action.unit_points",
    isMultiplier: false,
  },
  [RuleActionType.PROMOTER_SALES_PERFORMANCE_POINTS]: {
    type: RuleActionType.PROMOTER_SALES_PERFORMANCE_POINTS,
    labelKey: "rewards.action.promoter_sales_points",
    amountUnitKey: "rewards.action.unit_points_per_revenue",
    isMultiplier: false,
  },
};
